<template>
  <div v-if="notices.length" class="platform-notice">
    <van-notice-bar
      v-if="barVisible"
      class="platform-notice__bar"
      left-icon="volume-o"
      :scrollable="true"
      :color="barColor"
      :background="barBackground"
      @click="openDetail(0)"
    >
      {{ barText }}
    </van-notice-bar>

    <!-- van-popup：挂到 body + 锁滚动，避免穿透带动首页滚动/闪烁 -->
    <van-popup
      v-model="popupVisible"
      round
      :lock-scroll="true"
      :close-on-click-overlay="true"
      get-container="body"
      class="platform-notice-popup"
      :style="{ width: '85%', maxWidth: '320px' }"
      @closed="onPopupClosed"
    >
      <div class="platform-notice-dialog">
        <div class="platform-notice-dialog__header">
          <h3 class="platform-notice-dialog__title">{{ current.title || '平台公告' }}</h3>
        </div>
        <div class="platform-notice-dialog__content">
          <p class="platform-notice-dialog__message">{{ current.noticeTxt || '暂无内容' }}</p>
        </div>
        <div class="platform-notice-dialog__actions">
          <button
            v-if="notices.length > 1"
            type="button"
            class="platform-notice-dialog__btn platform-notice-dialog__btn--ghost"
            :disabled="index <= 0"
            @click="prevNotice"
          >上一条</button>
          <button
            v-if="notices.length > 1"
            type="button"
            class="platform-notice-dialog__btn platform-notice-dialog__btn--ghost"
            :disabled="index >= notices.length - 1"
            @click="nextNotice"
          >下一条</button>
          <button
            type="button"
            class="platform-notice-dialog__btn platform-notice-dialog__btn--primary"
            @click="closePopup"
          >知道了</button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
const READ_KEY = 'ecms_notice_popup_read'

function todayStr () {
  const d = new Date()
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function readMap () {
  try {
    return JSON.parse(localStorage.getItem(READ_KEY) || '{}') || {}
  } catch (e) {
    return {}
  }
}

function writeMap (map) {
  localStorage.setItem(READ_KEY, JSON.stringify(map || {}))
}

function noticeVersion (n) {
  return n.popupVersion == null ? 1 : Number(n.popupVersion)
}

function noticeFreq (n) {
  return (n && n.popupFreq) || 'once'
}

export default {
  name: 'PlatformNoticeBar',
  props: {
    /** mall_home / cart / movie_home / cake_home / gift_home */
    pageCode: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      notices: [],
      popupVisible: false,
      index: 0,
      autoPopupSession: false,
      loaded: false
    }
  },
  computed: {
    current () {
      return this.notices[this.index] || {}
    },
    barText () {
      // 标题 + 内容一起滚动；多条用间隔符拼接
      const parts = this.notices.map(n => {
        const title = (n.title || '').trim()
        const txt = (n.noticeTxt || '').trim().replace(/\s+/g, ' ')
        if (title && txt) return `${title}：${txt}`
        return title || txt
      }).filter(Boolean)
      return parts.join('　　') || '平台公告'
    },
    barVisible () {
      return this.notices.length > 0
    },
    barColor () {
      return '#ed6a0c'
    },
    barBackground () {
      return '#fffbe8'
    }
  },
  mounted () {
    this.loadNotices()
  },
  // keep-alive 再次进入：按频率尝试再弹（once/daily 由已读挡住）
  activated () {
    if (this.loaded) {
      this.tryAutoPopup()
    }
  },
  // 弹窗挂在 body：切页时必须关掉，否则会盖住下一页
  deactivated () {
    if (this.popupVisible) {
      // 未点「知道了」就离开：不记已读，返回后可按频率再弹
      this.autoPopupSession = false
      this.popupVisible = false
    }
  },
  beforeDestroy () {
    // 组件销毁时确保未残留锁滚动状态（van-popup 一般会自行恢复）
    this.popupVisible = false
  },
  watch: {
    pageCode () {
      this.loaded = false
      this.loadNotices()
    }
  },
  methods: {
    async loadNotices () {
      if (!this.pageCode) return
      try {
        const res = await this.$api.listActiveNotices(this.pageCode)
        if (res && res.code === 0) {
          this.notices = Array.isArray(res.data) ? res.data : []
          this.loaded = true
          this.$nextTick(() => this.tryAutoPopup())
        } else {
          this.notices = []
          this.loaded = true
        }
      } catch (e) {
        this.notices = []
        this.loaded = true
      }
    },
    shouldPopup (n) {
      if (!n || n.displayMode !== 'popup') return false
      const freq = noticeFreq(n)
      if (freq === 'every_visit') return true
      const ver = noticeVersion(n)
      const rec = readMap()[String(n.id)]
      // 新格式：{ v, d? }；无记录或格式不符则弹
      if (!rec || typeof rec !== 'object') return true
      if (freq === 'daily') {
        if (Number(rec.v) !== ver) return true
        return rec.d !== todayStr()
      }
      // once（默认）：同版本已读则不弹
      return Number(rec.v) !== ver
    },
    markPopupRead (list) {
      const map = readMap()
      const today = todayStr()
      ;(list || []).forEach(n => {
        if (!n || n.displayMode !== 'popup') return
        const freq = noticeFreq(n)
        // every_visit 不写已读，下次进入仍弹
        if (freq === 'every_visit') return
        const ver = noticeVersion(n)
        if (freq === 'daily') {
          map[String(n.id)] = { v: ver, d: today }
        } else {
          map[String(n.id)] = { v: ver }
        }
      })
      writeMap(map)
    },
    tryAutoPopup () {
      if (this.popupVisible) return
      const unread = this.notices.filter(n => this.shouldPopup(n))
      if (!unread.length) return
      const first = unread[0]
      const idx = this.notices.findIndex(n => n.id === first.id)
      this.autoPopupSession = true
      this.openDetail(idx >= 0 ? idx : 0)
    },
    openDetail (idx) {
      if (!this.notices.length) return
      this.index = Math.max(0, Math.min(idx || 0, this.notices.length - 1))
      this.popupVisible = true
    },
    prevNotice () {
      if (this.index <= 0) return
      this.index -= 1
    },
    nextNotice () {
      if (this.index >= this.notices.length - 1) return
      this.index += 1
    },
    closePopup () {
      this.popupVisible = false
    },
    onPopupClosed () {
      if (this.autoPopupSession) {
        this.markPopupRead(this.notices.filter(n => n.displayMode === 'popup'))
        this.autoPopupSession = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.platform-notice {
  width: 100%;
  position: relative;
  z-index: 6;
  padding: 0.25rem 0.5rem 0;
  box-sizing: border-box;
}

.platform-notice__bar {
  height: 2.2rem;
  line-height: 2.2rem;
  padding: 0 0.75rem;
  font-size: 0.8rem;
  border-radius: 0.375rem;
}

.platform-notice-dialog {
  background: #ffffff;
  width: 100%;
  max-height: 60vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.platform-notice-dialog__header {
  padding: 16px 20px 12px;
  text-align: center;
  border-bottom: 1px solid #f5f5f5;
  flex-shrink: 0;
}

.platform-notice-dialog__title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
  margin: 0;
  line-height: 1.4;
}

.platform-notice-dialog__content {
  padding: 14px 20px 12px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
  min-height: 48px;
  overscroll-behavior: contain;
}

.platform-notice-dialog__message {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
}

.platform-notice-dialog__actions {
  display: flex;
  align-items: center;
  min-height: 48px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.platform-notice-dialog__btn {
  flex: 1;
  height: 48px;
  border: none;
  border-left: 1px solid #f0f0f0;
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  &:first-child {
    border-left: none;
  }
  &:disabled {
    color: #c8c9cc;
    cursor: not-allowed;
  }
}

.platform-notice-dialog__btn--ghost {
  color: #666;
}

.platform-notice-dialog__btn--primary {
  color: #ee0a24;
  font-weight: 600;
}
</style>

<style lang="scss">
/* get-container=body 后需非 scoped 微调 van-popup 内边距 */
.platform-notice-popup.van-popup {
  overflow: hidden;
  padding: 0;
}
</style>
